<template>
  <div id="customerSection" class="columns-container container-spacing">
    <div class="column">
      <div class="change-order-page__header-left section-header">
        <h3>
          {{ i18n.t('/change-order/sections/customer-data') }}
        </h3>
      </div>
      <div class="customer-data-input-container">
        <div class="eigenaar-id-panel">
          <div class="eigenaar-id-number">
            <label class="label">
              {{ i18n.t('/change-order/customer-data/owner-id') }}
            </label>
            <span>{{ getOriginalCustomerId }}</span>
          </div>
          <div class="eigenaar-id-name">
            <label class="label">
              {{ i18n.t('/change-order/customer-data/client-name') }}
            </label>
            <span>{{ getOriginalCustomerName }}</span>
          </div>
          <div class="lessee-id-number">
            <label class="label">
              {{ i18n.t('/change-order/customer-data/lessee-id') }}
            </label>
            <span>{{ getOriginalLesseeId }}</span>
          </div>
          <div class="bestuurder-id-number">
            <label class="label">
              {{ i18n.t('/change-order/customer-data/manager-id') }}
            </label>
            <span>{{ getOriginalDriverId }}</span>
          </div>
          <div class="customerAssignmentDate">
            <label class="label">
              {{ i18n.t('/change-order/customer-data/customer-added-date') }}
            </label>
            <span>{{ getOriginalCustomerDate }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="column">
      <div class="change-order-page__header-right section-header">
        <h3>
          {{ i18n.t('/change-order/sections/customer-data') }}
        </h3>
      </div>
      <div class="customer-data-input-container">
        <div>
          <span v-if="hasChangedCustomerId" class="confirmation-message">
            <i class="fas fa-arrows-rotate"></i>&nbsp;
            <strong>
              {{ i18n.t('/change-order/generic/modified') }}
            </strong>
            &nbsp; {{ i18n.t('/change-order/generic/modified-confirmation') }}
          </span>
          <div class="eigenaar-id-panel">
            <div class="eigenaar-id-number">
              <label class="label">
                {{ i18n.t('/change-order/customer-data/owner-id') }}
              </label>
              <span :class="determineCustomerChangedClass">
                {{ getChangedCustomerId }}
              </span>
            </div>
            <div class="eigenaar-id-name">
              <label class="label">
                {{ i18n.t('/change-order/customer-data/client-name') }}
              </label>
              <span :class="determineCustomerChangedClass">
                {{ getChangedCustomerName }}
              </span>
            </div>
            <div class="customer-order-button-panel">
              <app-button
                v-if="isChangeButtonVisible"
                data-qa="enter-edit-mode"
                :title="retrieveLockedReasons"
                @click="enterEditMode"
              >
                <i :class="determineChangedClass"></i>&nbsp;
                {{ i18n.t('/edit button label') }}
              </app-button>
              <app-button
                v-if="hasCustomerId && isChangeButtonVisible"
                data-qa="remove-eigenaar-id"
                :title="retrieveLockedReasons"
                @click="removeOwnerID"
              >
                <i :class="determineChangedClass"></i>&nbsp;
                {{ i18n.t('/change-order/actions/delete') }}
              </app-button>
            </div>
          </div>
        </div>

        <modal
          v-if="showSearchPanel"
          class="search-owner-panel"
          :title="'Zoek klant of bedrijf'"
          @close="showSearchPanel = false"
        >
          <div>
            <search-customer :disable-add-customer="false" @select-customer="setCustomer" />
          </div>
        </modal>
      </div>
    </div>
  </div>
</template>
<script>
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import ChangeOrderStatus from '@/helpers/enums/changeOrderStatus'
import { mapGetters } from 'vuex'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import SearchCustomer from '@/components/changeOrder/presentational/organisms/searchCustomer/SearchCustomer.vue'
import { EventBus } from '@/plugins/event-bus'

export default {
  components: { SearchCustomer, Modal, AppButton },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showSearchPanel: false,
      customerID: null,
      customerName: null,
    }
  },
  computed: {
    ...mapGetters({
      changeOrder: 'changeOrder/get',
      dealerSettings: 'user/dealerSettings',
    }),
    hasChangedCustomerId() {
      return (
        this.changeOrder.Customer.OriginalCustomerId !== this.changeOrder.Customer.ChangedCustomerId
      )
    },
    isChangeButtonVisible() {
      return this.dealerSettings.isImporter
    },
    canCustomerChange() {
      return (
        this.isChangeButtonVisible &&
        this.changeOrder.Permissions.CanChangeCustomer.Allowed === true &&
        this.changeOrder.Customer.OrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.archived
      )
    },
    getOriginalCustomerId() {
      let originalCustomerId = this.changeOrder.Customer.OriginalCustomerId
      if (originalCustomerId !== null) {
        return originalCustomerId
      }
      return '-'
    },
    getOriginalCustomerName() {
      let originalCustomerName = this.changeOrder.Customer.OriginalCustomerName
      if (originalCustomerName !== null) {
        return originalCustomerName
      }
      return 'Niet beschikbaar'
    },
    getOriginalLesseeId() {
      let originalLesseeId = this.changeOrder.Customer.OriginalLesseeId
      if (originalLesseeId !== null) {
        return originalLesseeId
      }
      return '-'
    },
    getOriginalDriverId() {
      let originalDriverId = this.changeOrder.Customer.OriginalDriverId
      if (originalDriverId !== null) {
        return originalDriverId
      }
      return '-'
    },
    getOriginalCustomerDate() {
      let customerAssignmentDate = this.changeOrder.Customer.OriginalCustomerAssignmentDate
      if (customerAssignmentDate !== null) {
        return customerAssignmentDate
      }
      return '-'
    },
    determineCustomerChangedClass() {
      if (this.hasChangedCustomerId) {
        return 'eigenaar-id-changed'
      }
      return ''
    },
    determineChangedClass() {
      if (this.canCustomerChange) {
        return 'fa-solid fa-pencil'
      } else {
        return 'fa-solid fa-lock'
      }
    },
    retrieveLockedReasons() {
      let lockedReasons = []
      if (!this.canCustomerChange) {
        if (!this.changeOrder.Permissions?.CanChangeCustomer?.Allowed) {
          lockedReasons.push(this.changeOrder.Permissions?.CanChangeCustomer?.Reason)
        }
        if (
          this.changeOrder.Customer?.OrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.MainOrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.MainOrderStatus === ChangeOrderStatus.archived
        ) {
          lockedReasons.push('Order has been submitted or deleted')
        }
      }
      return lockedReasons.join('\n')
    },
    getChangedCustomerId() {
      let changedCustomerId = this.changeOrder.Customer.ChangedCustomerId
      if (changedCustomerId !== null) {
        return changedCustomerId
      }
      return '-'
    },
    getChangedCustomerName() {
      let changedCustomerName = this.changeOrder.Customer.ChangedCustomerName
      if (changedCustomerName !== null) {
        return changedCustomerName
      }
      return 'Niet beschikbaar'
    },
    hasCustomerId() {
      return this.changeOrder.Customer.ChangedCustomerId
    },
  },
  mounted() {
    /*
       There is no one specific way that we are notified that a
       customer or company was selected in the <search-customer> component.
       For adding the customer an eventListener is used.
       For adding a company an event is sent on the eventBus.
     */
    EventBus.$on('changeOrder/companyUpdated', this.companyUpdated)
  },
  methods: {
    companyUpdated() {
      //Update CustomerStatus after company was added to customer
      this.$store.commit('changeOrder/setCustomerStatus')
      this.showSearchPanel = false
    },
    enterAddMode() {
      this.showSearchPanel = true
    },
    cancelAddMode() {
      this.showSearchPanel = false
      this.customerID = ''
      this.customerName = ''
    },
    confirmOwnerID() {
      this.changeOrder.Customer.ChangedCustomerId = this.customerID
      this.changeOrder.Customer.ChangedCustomerName = this.customerName
      this.showSearchPanel = false
      this.$store.commit('changeOrder/setCustomerStatus')
    },
    enterEditMode() {
      if (this.canCustomerChange) {
        this.customerID = this.changeOrder.Customer.ChangedCustomerId
        this.customerName = this.changeOrder.Customer.ChangedCustomerName
        this.enterAddMode()
      }
    },
    removeOwnerID() {
      if (this.canCustomerChange) {
        this.customerID = this.changeOrder.Customer.ChangedCustomerId = null
        this.customerName = this.changeOrder.Customer.ChangedCustomerName = null
        this.$store.commit('changeOrder/setCustomerStatus')
      }
    },
    setCustomer(customer) {
      this.showSearchPanel = false
      this.customerID = this.getCustomerID(customer)
      this.customerName = this.composeCustomerName(customer)
      this.confirmOwnerID()
    },
    getCustomerID(customer) {
      if (customer.PoId) {
        return customer.PoId
      } else {
        return customer.CustomerMagicNumber
      }
    },
    composeCustomerName(customer) {
      return (
        (customer.CustomerTitle ? customer.CustomerTitle + ' ' : '') +
        ((this.dealerSettings.autolineKeyloopEnabled
          ? customer.CustomerFirstName
          : customer.CustomerInitials
            ? customer.CustomerInitials + ' '
            : '') || '') +
        ((customer.CustomerMiddleName ? `${customer.CustomerMiddleName} ` : '') +
          (customer.CustomerFamilyName || ''))
      )
    },
  },
}
</script>
<style lang="scss" scoped>
.columns-container {
  display: flex;
  justify-content: space-between;
}

.container-spacing {
  margin-top: 50px;
}

.column {
  flex: 1;
  margin: 0 20px;
}

h3 {
  font-weight: 400;
  margin: 0;
}

.change-order {
  &-page__header-left {
    h3 {
      color: $black;
    }
  }

  &-page__header-right {
    h3 {
      color: $color-primary;
    }
  }
}

.search-owner-panel {
  z-index: 1000;
}

.section-header {
  border-bottom: 1px solid #a3b8c0;
  align-items: center;
  margin-bottom: 1rem;
}

.customer-data-input-container {
  margin-top: 40px;
}

.confirmation-message {
  background-color: #ffdd9c;
  font-size: 18px;
  padding: 10px;
}

.eigenaar-id-input-panel {
  label {
    font-weight: bold;
    margin-bottom: 3px;
  }
  input {
    width: 400px;
    border: 1px solid black;
  }
}

.eigenaar-id-panel {
  margin-top: 20px;
  label {
    font-weight: bold;
    margin-bottom: 2px;
  }
  span {
    padding: 5px;
  }
}

.eigenaar-id-name {
  margin-top: 2px;
  margin-left: 25px;
}

.lessee-id-number {
  margin-top: 10px;
}

.bestuurder-id-number {
  margin-top: 10px;
}

.customerAssignmentDate {
  margin-top: 20px;
}

.eigenaar-id-changed {
  background-color: #fff8b9;
}

.customer-order-button-panel {
  margin-top: 20px;
}

.edit-icon {
  margin-left: 10px;
  cursor: pointer;
}

:deep(.clickable-background) {
  button {
    position: relative;
    display: block;
    margin-top: 5px !important;
    margin-left: 0 !important;
  }
}
</style>
